import { useMemo } from 'react';
import { stringifyVariables } from 'urql';

import type {
  CompositionV3Ent,
  CompositionV4BreakdownReq,
  PayV3Req,
} from '@revelio/data-access';

import { useFilterContext } from '../../filters/contexts/FilterProvider';
import {
  type Filter,
  FilterId,
  FilterType,
  type PrimaryFilterId,
} from '../../filters/types/filters';
import { resolveV3EntForSelection } from '../../filters/utils/get-v3-entities';
import { getPayV3Variables } from '../../filters/utils/pay-v3/get-pay-v3-variables';
import { usePayData } from '../DataProvider/compensation/use-pay-data';
import { TOP_COMPANIES_MAX } from '../DataProvider/constants';
import { selectTopCompanyPool } from '../DataProvider/TopNDataProvider';
import {
  DataProviderQueryStatus,
  type DataProviderResult,
  type TableData,
} from '../DataProvider/types';
import { useStableReference } from '../DataProvider/utils/use-stable-reference';
import {
  buildCompositionV4Request,
  isCompositionV4RequestComplete,
  isSnapshotQuery,
} from './build-composition-v4-request';
import {
  getEntityBreakdowns,
  isCompositionV4BreakdownDimension,
} from './composition-v4-breakdowns';
import {
  convertCompositionV4CompaniesByEntity,
  dimensionFromPrimaryFilter,
} from './convert-composition-v4-data';
import type { PlannedQuery, TopCompaniesOf } from './plan-data-queries';
import type { QueryResult } from './resolve-items';
import { useCompositionV4Filters } from './use-composition-v4-filters';
import { useCompositionV4Query } from './use-composition-v4-query';
import {
  type CompositionV4Drilldown,
  narrowCompositionV4Request,
  useCompositionV4RequestTables,
} from './use-composition-v4-tables';

type EntitySelection = TopCompaniesOf['entity'];

const toEntities = (
  selections: readonly EntitySelection[]
): CompositionV3Ent[] =>
  selections
    .map((selection) => resolveV3EntForSelection(selection))
    .filter((ent): ent is CompositionV3Ent => ent !== null);

const useTopCompanies = ({
  primaryDimension,
  entities: selections,
  limit,
  within,
  filters,
  pause = false,
}: {
  primaryDimension: PrimaryFilterId;
  entities: readonly EntitySelection[];
  limit: number;
  within?: CompositionV4Drilldown;
  filters: Filter[];
  pause?: boolean;
}): DataProviderResult<CompositionV3Ent[]> => {
  const { usesStateBreakdownOverMsa } = useFilterContext();
  const entities = toEntities(selections);
  const payReq: PayV3Req = {
    ...getPayV3Variables({
      primaryDimension,
      effectiveFilters: filters,
      entitiesOverride: entities,
    }),
    options: {
      required_top_companies_length: TOP_COMPANIES_MAX,
      ...(usesStateBreakdownOverMsa && { geo_breakdown_state_over_msa: true }),
    },
  };
  const { data, status } = usePayData({
    variant: 'full',
    req: within ? narrowCompositionV4Request(payReq, within) : payReq,
    pause: pause || entities.length === 0,
  });

  const companies = useMemo(
    () => (data?.pay_v3 ? selectTopCompanyPool(data.pay_v3, limit) : undefined),
    [data, limit]
  );
  return { data: companies, status };
};

const primarySelections = (
  filters: Filter[],
  primaryDimension: PrimaryFilterId
): EntitySelection[] => {
  const primaryFilter = filters.find(({ id }) => id === primaryDimension);
  return primaryFilter?.type === FilterType.TREE
    ? primaryFilter.selections
    : [];
};

const withOnlyEntity = (
  filters: Filter[],
  { primaryDimension, entity }: TopCompaniesOf
): Filter[] =>
  filters.map((filter) =>
    filter.id === primaryDimension && filter.type === FilterType.TREE
      ? {
          ...filter,
          selections: filter.selections.filter(
            ({ id, levelId }) => id === entity.id && levelId === entity.levelId
          ),
        }
      : filter
  );

export const useTopCompaniesTables = ({
  tables,
  topCompaniesOf,
}: {
  tables: PlannedQuery['tables'];
  topCompaniesOf: TopCompaniesOf;
}): QueryResult | null => {
  const { primaryDimension, entity, limit } = topCompaniesOf;
  const { filters, isLoading } = useCompositionV4Filters({ primaryDimension });
  const topCompanies = useTopCompanies({
    primaryDimension,
    entities: [entity],
    limit,
    filters,
    pause: isLoading,
  });
  const { status } = topCompanies;
  const companies =
    status === DataProviderQueryStatus.Success ? topCompanies.data : undefined;

  const result = useCompositionV4RequestTables({
    primaryDimension: FilterId.COMPANY,
    tables,
    req: buildCompositionV4Request({
      dimension: FilterId.COMPANY,
      filters: withOnlyEntity(filters, topCompaniesOf),
      entities: companies ?? [],
      isSnapshot: isSnapshotQuery(tables),
    }),
    pause: !companies?.length || isLoading,
  });

  return useMemo(
    () => (companies?.length ? result : { data: undefined, status }),
    [companies, result, status]
  );
};

const areBreakdownsEqual = (
  previous: readonly CompositionV4BreakdownReq[],
  next: readonly CompositionV4BreakdownReq[]
): boolean =>
  previous === next ||
  stringifyVariables(previous) === stringifyVariables(next);

export const useTopCompaniesByEntity = ({
  primaryDimension,
  within,
  limit,
  pause = false,
}: {
  primaryDimension: PrimaryFilterId;
  within?: CompositionV4Drilldown;
  limit: number;
  pause?: boolean;
}): DataProviderResult<TableData> => {
  const { filters, isLoading } = useCompositionV4Filters({ primaryDimension });
  const selections = primarySelections(filters, primaryDimension);
  const topCompanies = useTopCompanies({
    primaryDimension,
    entities: selections,
    limit,
    within,
    filters,
    pause: pause || isLoading,
  });
  const { status } = topCompanies;
  const companies =
    status === DataProviderQueryStatus.Success ? topCompanies.data : undefined;

  const dimension = dimensionFromPrimaryFilter(primaryDimension);
  const breakdowns = useStableReference(
    isCompositionV4BreakdownDimension(dimension)
      ? getEntityBreakdowns(dimension, toEntities(selections))
      : [],
    areBreakdownsEqual
  );
  const pageReq = {
    ...buildCompositionV4Request({
      dimension: FilterId.COMPANY,
      filters,
      entities: companies ?? [],
      isSnapshot: true,
    }),
    breakdowns,
  };
  const req = within ? narrowCompositionV4Request(pageReq, within) : pageReq;
  const query = useCompositionV4Query({
    req,
    metrics: [],
    pause:
      pause ||
      isLoading ||
      !companies?.length ||
      breakdowns.length === 0 ||
      !isCompositionV4RequestComplete(req),
  });

  const table = useMemo(
    () =>
      query.data &&
      convertCompositionV4CompaniesByEntity({
        data: query.data,
        primaryDimension,
        breakdowns,
      }),
    [query.data, primaryDimension, breakdowns]
  );

  return useMemo(
    () =>
      companies?.length
        ? { data: table, status: query.status, error: query.error }
        : { data: undefined, status },
    [companies, table, query.status, query.error, status]
  );
};
